import {
  Text,
  Box,
  IconButton,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  useToast,
  Tooltip,
} from "@chakra-ui/react";

import Image from "next/image";
import {
  IconLI,
  IconTwitter,
  IconBookmark,
  IconVerified,
  IconLink,
  IconPlayVideo,
  IconYT,
  IconBookmarkFilled,
  IconLocationPin,
  IconGraduationCap,
  IconArticle,
} from "../../styles/icons";
import Link from "next/link";
import ReactPlayer from "react-player";
import { useSelector, useDispatch } from "react-redux";
import { useMemo, useState } from "react";
import { postData, deleteData } from "../utilities/axios";
import * as N from "../utilities/notifications";
import { updateProfileBookmarks } from "../redux/actions/bookmarks";
import LogInModal from "../authentication/LogInModal";
import BookingRequestModal from "../booking/initiate-booking/BookingRequestModal";
import { IsMobileView } from "../utilities/helpers";
import { Button } from "@headlessui/react";
import { useUser } from "../hooks/useUser";
import Head from "next/head";

export const ViewSectionProfileHeader = ({ profile, hasArticles = false }) => {
  const [loadedSession, token] = useUser();

  const {
    fullname,
    profileHeaderImage,
    links,
    pronoun,
    addresses,
    verified,
    profileVideo,
    profileImage,
    profileId,
    practitionerType,
    therapistTitles,
    userId,
    isSupervisor,
  } = profile;

  //Mobile View
  const mobileView = IsMobileView();
  const user = useSelector((state) => state.user.userDetails);

  // Set state
  const savedProfiles = useSelector((state) => state.bookmarks.profiles);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const loggedIn = useSelector((state) => state.user.JWTToken);
  const [showBookingRequestModal, setShowBookingRequestModal] = useState(false);
  const [videoHover, setVideoHover] = useState(false);

  const dispatch = useDispatch();
  const toast = useToast();
  const loginModalTitle = useMemo(() => "Please Login or Sign up", []);

  const {
    isOpen: isVideoOpen,
    onOpen: onVideoOpen,
    onClose: onVideoClose,
  } = useDisclosure();

  // Helpers
  const addToSavedList = () => {
    const searchQuery = {
      profileId: profileId,
      practitionerType: practitionerType,
    };

    try {
      postData(
        token,
        searchQuery,
        `${process.env.NEXT_PUBLIC_NEW_SEARCH_URL}save`,
        process.env.NEXT_PUBLIC_NEW_SEARCH_APIKEY,
      ).then(() => {
        dispatch(updateProfileBookmarks([...savedProfiles, { profileId }]));
      });
    } catch (error) {
      console.error("ERROR saving bookmark", error);
      toast(N.genericFail("Failed to save bookmark"));
    }
  };

  const removeFromSavedList = () => {
    const searchQuery = {
      profileId: profileId,
      practitionerType: practitionerType,
    };

    try {
      deleteData(
        token,
        searchQuery,
        `${process.env.NEXT_PUBLIC_NEW_SEARCH_URL}save`,
        process.env.NEXT_PUBLIC_NEW_SEARCH_APIKEY,
      ).then(() => {
        dispatch(
          updateProfileBookmarks(
            savedProfiles.filter(
              (elem) => Number(elem.profileId) !== Number(profileId),
            ),
          ),
        );
      });
    } catch (error) {
      console.error("ERROR removing bookmark", error);
      toast(N.genericFail("Failed to remove bookmark"));
    }
  };

  const renderMediaButton = (button) => {
    let icon = null;
    switch (button.type) {
      case "website":
        icon = <IconLink />;
        break;
      case "twitter":
        icon = <IconTwitter />;
        break;
      case "linkedIn":
        icon = <IconLI />;
        break;
      case "youTube":
        icon = <IconYT />;
        break;
      default:
        return null;
    }
    if (button.url === null || button.url.length < 1) return;

    //Check for http header
    const httpHeader =
      button.url.startsWith("http") ||
      button.url.startsWith("HTTP") ||
      button.url.startsWith("Http");

    const fullURL = httpHeader ? button.url : `https://${button.url}`;
    return (
      <Link href={fullURL} target="_blank" prefetch={false}>
        {button.type === "website" ? (
          <Button
            className="bg-white flex gap-2 items-center font-bold border border-brand text-brand hover:text-white hover:bg-brand transition rounded-full px-4 py-2"
            key={button.type}
            aria-label={button.type}
          >
            {icon}
            {button.type}
          </Button>
        ) : (
          <IconButton
            size={mobileView ? "sm" : "md"}
            key={button.type}
            variant="outline"
            color="primary.500"
            aria-label={button.type}
            fontSize="20px"
            p={0}
            icon={icon}
          />
        )}
      </Link>
    );
  };

  const handleSessionButton = () => {
    if (isVideoOpen) {
      onVideoClose();
    }
    loggedIn ? setShowBookingRequestModal(true) : setShowLoginModal(true);
  };

  const isSelf = useMemo(() => !!(user && user.id === userId), [user, userId]);

  return (
    <div className="flex relative flex-col bg-white rounded-t-[20px] overflow-hidden">
      <Head>
        <title>{`Welldoing Counsellors - ${fullname}`}</title>
      </Head>
      {showBookingRequestModal && (
        <BookingRequestModal
          show={showBookingRequestModal}
          close={() => setShowBookingRequestModal(false)}
          practitionerId={userId}
          profileId={profileId}
          practitionerName={fullname}
          practitionerType={practitionerType}
        />
      )}
      {showLoginModal && (
        <LogInModal
          show={showLoginModal}
          onCloseModal={(success) => {
            setShowBookingRequestModal(success);
            setShowLoginModal(false);
          }}
          title={`Please sign up or log in to contact ${fullname}`}
          therapistName={fullname}
          bookingRequest
          signUpChoice="bookingRequest"
          noRedirect
        />
      )}
      <Modal isOpen={isVideoOpen} onClose={onVideoClose} minWidth="750px">
        <ModalOverlay />
        <ModalContent maxW="750px">
          <ModalHeader>{fullname}</ModalHeader>
          <ModalCloseButton />
          <ModalBody mx="auto">
            <ReactPlayer width="100%" playing url={profileVideo} />
          </ModalBody>

          <ModalFooter>
            {!isSelf && (
              <Button
                className="bg-white w-full font-bold items-center border border-brand text-brand hover:text-white hover:bg-brand transition rounded-full px-4 py-2"
                onClick={() => handleSessionButton()}
                aria-label="Book Session"
                id="make-contact-button-3"
              >
                Make contact
              </Button>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>
      <Box
        className="flex justify-center w-full sm:w-[200px] mt-[70px] sm:mx-5"
        height={200}
        position="absolute"
        top="0"
        left="0"
        zIndex="100"
      >
        <Box
          width={200}
          height={200}
          borderRadius="50%"
          overflow="hidden"
          position="relative"
          border={
            profileVideo !== null
              ? videoHover
                ? "9px solid #587500"
                : "9px solid #F6F4EF"
              : "9px solid #ffffff"
          }
        >
          <Image
            width={200}
            height={200}
            alt="practitioner photo"
            style={{ objectFit: "contain", objectPosition: "center" }}
            src={
              profileImage !== null && Object.keys(profileImage).length !== 0
                ? profileImage
                : "/images/profileHeader.jpg"
            }
            placeholder="blur"
            unoptimized
            blurDataURL="data:image/jpeg;base64,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"
          />
        </Box>
        {profileVideo !== null && (
          <Box
            width="60px"
            height="60px"
            pointer="cursor"
            display="flex"
            justifyContent="flex-end"
            border="none"
            p={0}
            m={0}
            alignItems="flex-end"
            position="absolute"
            bottom="0"
            right="0"
            zIndex="10"
            onClick={onVideoOpen}
          >
            <IconButton
              bg="#587500"
              color="white"
              height="49px"
              width="49px"
              variant="solidTertiary"
              aria-label="Play video"
              icon={<IconPlayVideo />}
              onMouseEnter={() => setVideoHover(true)}
              onMouseLeave={() => setVideoHover(false)}
            />
          </Box>
        )}
      </Box>
      <div className="h-[170px] w-full relative">
        {profileHeaderImage !== null ? (
          <Image
            src={profileHeaderImage}
            placeholder="blur"
            blurDataURL="data:image/jpeg;base64,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"
            layout="fill"
            style={{
              objectFit: "cover",
              objectPosition: "center",
            }}
            unoptimized
            alt="profile header image"
            priority
          />
        ) : (
          <div className="w-full h-full bg-brand" />
        )}
      </div>
      <div className="p-[20px_20px_30px_30px] flex flex-col gap-[23px] z-[101] pointer-events-none">
        <div className="hidden pointer-events-none sm:flex items-center w-full justify-end">
          {hasArticles && (
            <a
              href="#articles"
              className="flex flex-row items-center cursor-pointer gap-3 mr-3 font-bold bg-brand border border-brand text-white hover:text-brand hover:bg-white transition rounded-full p-3 pointer-events-auto"
            >
              <IconArticle className="h-5 w-5" />
              Articles
            </a>
          )}
          {savedProfiles.find(
            (elem) => Number(elem.profileId) === Number(profileId),
          ) ? (
            <Tooltip
              hasArrow
              label="Remove from bookmarks"
              className="pointer-events-auto"
            >
              <Button
                className="bg-brand border border-brand text-white hover:text-brand hover:bg-white transition rounded-full p-3 pointer-events-auto"
                aria-label="Remove bookmark"
                onClick={() =>
                  token ? removeFromSavedList() : setShowLoginModal(true)
                }
              >
                <IconBookmarkFilled className="h-5 w-5" />
              </Button>
            </Tooltip>
          ) : (
            <Button
              aria-label="Bookmark profile"
              className="bg-white flex gap-3 font-bold items-center border border-brand text-brand hover:text-white hover:bg-brand transition rounded-full p-3 pointer-events-auto"
              onClick={() =>
                token ? addToSavedList() : setShowLoginModal(true)
              }
            >
              <IconBookmark className="h-5 w-5" />
              Bookmark
            </Button>
          )}
        </div>
        {mobileView && !isSelf && (
          <div className="fixed bottom-0 right-0 left-0 z-[1000] bg-white p-5">
            <Button
              className="bg-brand w-full font-bold border border-brand text-white hover:text-brand hover:bg-white transition rounded-full px-4 py-2 pointer-events-auto"
              width="100%"
              onClick={() => handleSessionButton()}
              aria-label="Book Session"
              id="make-contact-button-4"
            >
              Make contact
            </Button>
          </div>
        )}
        <div className="flex flex-col pt-[40px] w-full gap-2 mt-[70px] sm:mt-0 pointer-events-auto">
          <div className="flex flex-col sm:flex-row items-center w-full">
            <Text as="h1" className="w-max text-nowrap flex-1" textStyle="h3">
              {fullname}
            </Text>
            {pronoun?.length > 0 && !pronoun[0]?.label.includes("refer") && (
              <Text textStyle="h7" marginLeft="7px" marginTop="4px">
                ({pronoun[0]?.label})
              </Text>
            )}
            <Box
              position="relative"
              display={{ base: "flex", md: "flex" }}
              width="100%"
              justifyContent={{ base: "flex-start", md: "flex-end" }}
              alignItems="center"
              p="0px"
              paddingTop={{ base: "8px", md: "0px" }}
            >
              {links?.map((item) => {
                return renderMediaButton(item);
              })}
            </Box>
          </div>
          <p className="text-brand-alt font-bold">
            {therapistTitles.map((elem) => elem.label).join(", ")}
          </p>
          <div className="flex items-center gap-2">
            {addresses.length > 0 ? (
              <div>
                {addresses.map((address) => (
                  <div className="flex items-center gap-2">
                    <IconLocationPin size="20px" />
                    <p className="font-bold">
                      {`${address.city}, ${address.postcode}`}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              "Online only"
            )}
          </div>
          <div className="flex items-center gap-2">
            <Tooltip
              hasArrow
              label={`Practitioner ${
                verified ? "has been verified" : "is pending verification"
              } with their appropriate professional body by the Welldoing team.`}
              bg="primary.100"
              color="brand.300"
              placement="bottom"
            >
              <div className="flex items-center w-min">
                {verified ? (
                  <IconVerified size="20px" color="#8FAE30" />
                ) : (
                  <IconVerified size="20px" color="tomato" />
                )}
              </div>
            </Tooltip>

            <p className="font-bold">
              {verified ? "Verified therapist" : "Pending verification"}
            </p>
          </div>
          {isSupervisor === 1 && (
            <div className="gap-[7px] w-min flex items-center">
              <IconGraduationCap size="20px" />
              <span className="font-bold">Supervisor</span>
            </div>
          )}
        </div>
        <div className="flex sm:hidden items-center w-full justify-start">
          {savedProfiles.find(
            (elem) => Number(elem.profileId) === Number(profileId),
          ) ? (
            <Button
              className="bg-white flex gap-3 font-bold items-center border border-red-400 text-red-400 hover:text-white hover:bg-red-400 transition rounded-full p-3"
              aria-label="Remove bookmark"
              onClick={() =>
                token ? removeFromSavedList() : setShowLoginModal(true)
              }
            >
              <IconBookmarkFilled className="h-5 w-5" />
              Remove bookmark
            </Button>
          ) : (
            <Button
              aria-label="Bookmark profile"
              className="bg-white flex gap-3 font-bold items-center border border-brand text-brand hover:text-white hover:bg-brand transition rounded-full p-3"
              onClick={() =>
                token ? addToSavedList() : setShowLoginModal(true)
              }
            >
              <IconBookmark className="h-5 w-5" />
              Bookmark
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
